import {useEffect, useMemo, useState} from 'react';
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {generateUrl} from "v4/services/api.service";
import {PARAMS_REQUESTS} from "v4/data/apiRoutes";
import useAdminListEditor from "v4/features/admin/adminForms/components/AdminListEditor/hooks/useAdminListEditor";

export default function useQuoteState(rawValuesFromAdmin) {
    const {t} = usePolTranslation();

    const [quoteStateReasonsOptions, setQuoteStateReasonsOptions] = useState(null);
    const customerId = useMemo(() => rawValuesFromAdmin[0]?.customerId, [rawValuesFromAdmin]);

    useEffect(() => {
        fetch(generateUrl(PARAMS_REQUESTS, {customerId}))
            .then(response => response.json())
            .then(({data}) => {
                const quoteReason = data?.['hydra:member']?.find(item => item.name === 'QuoteReason')?.params;
                if (quoteReason) {
                    setQuoteStateReasonsOptions(quoteReason.map(item => ({value: item.id, label: item.name})));
                }
            });
    }, [customerId]);

    const quoteStateStatusOptions = useMemo(() => ([
        {value: "isAbandoned", label: t('is_abandoned')},
        {value: "inProgress", label: t('in_progress')},
        {value: "isWon", label: t('is_won')},
        {value: "isLost", label: t('is_lost')},
        {value: "isDiffered", label: t('is_differed')},
        {value: "isNotCountForStats", label: t('is_not_count_for_stats')},
        {value: "isUnSelectable", label: t('is_un_selectable')},
    ]), [t]);

    const listEditor = useAdminListEditor({
        rawValuesFromAdmin,
        entityCode: 'QuoteState',
        mapInputs: (item) => ({
            itemId: item.id,
            value: item.name,
            extra: {
                quoteStateStatus: getQuoteStateStatus(item),
                quoteStateReasons: item.reasons?.map(reason => reason.id) ?? [],
            },
        }),
        mapSubmitItem: (item, index) => ({
            id: item.itemId,
            name: item.value,
            order: index + 1,
            "@type": "QuoteState",
            isWon: item.extra.quoteStateStatus.includes('isWon'),
            isLost: item.extra.quoteStateStatus.includes('isLost'),
            isAbandoned: item.extra.quoteStateStatus.includes('isAbandoned'),
            isDiffered: item.extra.quoteStateStatus.includes('isDiffered'),
            isNotCountForStats: item.extra.quoteStateStatus.includes('isNotCountForStats'),
            isUnSelectable: item.extra.quoteStateStatus.includes('isUnSelectable'),
            inProgress: item.extra.quoteStateStatus.includes('inProgress'),
            customerId: customerId,
            reasons: item.extra.quoteStateReasons.map(reason => ({
                id: reason,
                name: quoteStateReasonsOptions?.find(option => option.value === reason)?.label,
                "@type": "QuoteReason",
                customerId: customerId,
            })),
        }),
        createEmptyItem: () => ({value: '', extra: {quoteStateStatus: [], quoteStateReasons: []}}),
    });

    const getQuoteStateReasonsOptions = (index) => {
        const selectedOptions = listEditor.methods.getValues('quoteState').map(item => item.extra.quoteStateReasons).flat();
        const options = (quoteStateReasonsOptions ?? []).filter(item => !selectedOptions.includes(item.value));
        const currentValue = listEditor.methods.watch(`quoteState.${index}.extra.quoteStateReasons`);
        if (currentValue?.length) {
            options.push(...currentValue.map(item => quoteStateReasonsOptions?.find(option => option.value === item)).filter(Boolean));
        }

        return options ?? [];
    };

    return {
        ...listEditor,
        quoteStateStatusOptions,
        showReasons: !!quoteStateReasonsOptions,
        getQuoteStateReasonsOptions,
    };
}

function getQuoteStateStatus(item) {
    return Object.keys(item).reduce((acc, curr) => {
        if ((curr.startsWith('is') || curr.startsWith('in')) && item[curr]) {
            acc.push(curr);
        }

        return acc;
    }, []);
}
